import './App.css';
import styled, { ThemeProvider, keyframes } from 'styled-components';
import { darkTheme, lightTheme } from './utils/Theme'
import Navbar from './components/Navbar';
import Hero from './components/HeroSection';
import Skills from './components/Skills';
import Education from './components/Education';
import Experience from './components/Experience';
import Projects from './components/Projects';
import ProjectDetails from './components/ProjectDetails';
import { BrowserRouter as Router } from 'react-router-dom';
import Contact from './components/Contact';
import Footer from './components/Footer';
import React, { useState, useEffect } from 'react';

// Floating orb animation
const floatOrb1 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(100px, -50px) scale(1.1); }
  50% { transform: translate(-50px, 100px) scale(0.9); }
  75% { transform: translate(-100px, -30px) scale(1.05); }
`;

const floatOrb2 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(-80px, 60px) scale(0.95); }
  50% { transform: translate(60px, -80px) scale(1.1); }
  75% { transform: translate(40px, 40px) scale(1); }
`;

const floatOrb3 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, 80px) scale(1.05); }
  66% { transform: translate(-70px, -60px) scale(0.95); }
`;

const Body = styled.div`
  background: ${({ theme }) => theme.gradient_hero};
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  transition: all 0.3s ease;
  position: relative;
`;

// Animated grid background
const GridBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(${({ theme }) => theme.primary + '08'} 1px, transparent 1px),
    linear-gradient(90deg, ${({ theme }) => theme.primary + '08'} 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
`;

// Glowing orbs for ambiance
const GlowingOrb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.4;
`;

const Orb1 = styled(GlowingOrb)`
  width: 400px;
  height: 400px;
  background: ${({ theme }) => theme.primary};
  top: 10%;
  left: 5%;
  animation: ${floatOrb1} 20s ease-in-out infinite;
`;

const Orb2 = styled(GlowingOrb)`
  width: 300px;
  height: 300px;
  background: ${({ theme }) => theme.accent};
  bottom: 20%;
  right: 10%;
  animation: ${floatOrb2} 25s ease-in-out infinite;
`;

const Orb3 = styled(GlowingOrb)`
  width: 250px;
  height: 250px;
  background: ${({ theme }) => theme.secondary};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${floatOrb3} 30s ease-in-out infinite;
`;

const Wrapper = styled.div`
  background: linear-gradient(
    38.73deg, 
    ${({ theme }) => theme.accent + '15'} 0%, 
    transparent 50%), 
  linear-gradient(
    141.27deg, 
    transparent 50%, 
    ${({ theme }) => theme.primary + '15'} 100%);
  width: 100%;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 98%, 0 100%);
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

function App() {
  // Check for saved theme preference or use system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check if user prefers dark mode
    return window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [darkMode, setDarkMode] = useState(getInitialTheme());
  const [openModal, setOpenModal] = useState({ state: false, project: null });

  // Toggle theme function
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Router>
        <Body>
          {/* Animated Background Elements */}
          <GridBackground />
          <Orb1 />
          <Orb2 />
          <Orb3 />

          <ContentWrapper>
            <Navbar darkMode={darkMode} toggleTheme={toggleTheme} />
            <Hero />
            <Wrapper>
              <Skills />
              <Experience />
            </Wrapper>
            <Projects openModal={openModal} setOpenModal={setOpenModal} />
            <Wrapper>
              <Education />
              <Contact />
            </Wrapper>
            <Footer />
            {openModal.state &&
              <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />
            }
          </ContentWrapper>
        </Body>
      </Router>
    </ThemeProvider>
  );
}

export default App;